import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useI18n } from '../context/I18nContext';
import { motion, AnimatePresence } from 'framer-motion';
import { jsPDF } from 'jspdf';
import { FiAlertCircle, FiAlertTriangle, FiArchive, FiCalendar, FiCheckCircle, FiEdit2, FiFileText, FiFolder, FiInfo, FiPlus, FiRotateCcw, FiTrash2 } from 'react-icons/fi';
import useIsMobile from '../hooks/useIsMobile';
import { getUserKey } from '../utils/userStorage';
import { updateProjects } from '../services/projectService';

// SMM PLATFORMALARI UCHUN IKONKALAR
const PlatformIcon = ({ name, size = 16 }) => {
  switch (name) {
    case 'YouTube':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="#ef4444" style={{ display: 'block' }}>
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
        </svg>
      );
    case 'Instagram':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#ec4899" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
          <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
          <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
          <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
        </svg>
      );
    case 'Facebook':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="#1877f2" style={{ display: 'block' }}>
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      );
    case 'Telegram':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="#0ea5e9" style={{ display: 'block' }}>
          <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.503-1.359 8.627-.168.9-.5 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.481-.428-.008-1.252-.241-1.865-.44-.751-.244-1.348-.373-1.296-.788.027-.216.324-.437.893-.664 3.505-1.524 5.844-2.529 7.018-3.013 3.344-1.383 4.039-1.623 4.493-1.631.1.002.321.027.465.145.121.101.154.241.168.341.015.102.033.334.017.502z"/>
        </svg>
      );
    default:
      return null;
  }
};

const defaultProjects = [];

// --- ANIMATSIYA VARIANTLARI ---
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 300, damping: 24 } }
};

const tabVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.3 } },
  exit: { opacity: 0, x: 20, transition: { duration: 0.2 } }
};

const ProjectList = () => {
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const { t } = useI18n();
  const isMobile = useIsMobile();

  const formatOptions = ['reels', 'carousel', 'post', 'animation', 'story'];

  const getFormatLabel = (format) => {
    return t(`projectList.formats.${format}`) || format;
  };

  const getActiveContentText = (count) => t('projectList.labels.activeContentCount').replace('{count}', count);

  const getTodayString = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const todayStr = getTodayString();

  const [projects, setProjects] = useState(() => {
    const savedProjects = localStorage.getItem(getUserKey('projects'));
    if (savedProjects) {
      return JSON.parse(savedProjects);
    }
    return defaultProjects;
  });

  useEffect(() => {
    localStorage.setItem(getUserKey('projects'), JSON.stringify(projects));
    updateProjects(projects).catch(() => {});
  }, [projects]);

  const [toast, setToast] = useState({ show: false, message: "", status: "default" });
  const [confirmDialog, setConfirmDialog] = useState(null);
  
  const [isArchiveDrawerOpen, setIsArchiveDrawerOpen] = useState(false);
  const [archiveTab, setArchiveTab] = useState('projects'); 

  const visibleProjects = projects.filter(p => !p.isArchived);
  
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  
  useEffect(() => {
    if (!selectedProjectId && visibleProjects.length > 0) {
      setSelectedProjectId(visibleProjects[0].id);
    } else if (visibleProjects.length === 0) {
      setSelectedProjectId(null);
    }
  }, [visibleProjects, selectedProjectId]);

  const activeProject = projects.find(p => p.id === selectedProjectId);
  const visiblePosts = activeProject ? activeProject.posts.filter(post => !post.isArchived) : [];

  const archivedProjects = projects.filter(p => p.isArchived);
  const archivedPosts = [];
  projects.forEach(proj => {
    proj.posts.forEach(post => {
      if (post.isArchived) {
        archivedPosts.push({ ...post, projectName: proj.name, projectId: proj.id });
      }
    });
  });

  const [showAddProjectModal, setShowAddProjectModal] = useState(false);
  const [showEditProjectModal, setShowEditProjectModal] = useState(false);
  const [showAddContentModal, setShowAddContentModal] = useState(false);
  const [showEditContentModal, setShowEditContentModal] = useState(false); 

  const [projName, setProjName] = useState("");
  const [phone, setPhone] = useState("");
  const [targetBudget, setTargetBudget] = useState("");

  const [editProjectId, setEditProjectId] = useState(null);
  const [editProjName, setEditProjName] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editTargetBudget, setEditTargetBudget] = useState("");

  const [contentTargetProjectId, setContentTargetProjectId] = useState("");
  const [postTitle, setPostTitle] = useState("");
  const [postFormat, setPostFormat] = useState('post');
  const [isFormatOpen, setIsFormatOpen] = useState(false);
  const [postDate, setPostDate] = useState("");
  const [postIdea, setPostIdea] = useState("");
  const [platformsData, setPlatformsData] = useState({
    YouTube: { checked: true, status: "pending", color: "#ef4444" },
    Instagram: { checked: true, status: "pending", color: "#ec4899" },
    Facebook: { checked: true, status: "pending", color: "#1877f2" },
    Telegram: { checked: true, status: "pending", color: "#0ea5e9" }
  });

  const [editPostId, setEditPostId] = useState(null);
  const [editPostTitle, setEditPostTitle] = useState("");
  const [editPostFormat, setEditPostFormat] = useState('post');
  const [isEditFormatOpen, setIsEditFormatOpen] = useState(false);
  const [editPostDate, setEditPostDate] = useState("");
  const [editPostIdea, setEditPostIdea] = useState("");
  const [editPlatformsData, setEditPlatformsData] = useState({});

    const getStatusColors = (status, isLightMode) => {
    switch (status) {
      case 'published': return { bg: isLightMode ? 'rgba(34, 197, 94, 0.15)' : 'rgba(34, 197, 94, 0.2)', text: isLightMode ? '#16a34a' : '#4ade80', border: 'rgba(34, 197, 94, 0.4)' };
      case 'cancelled': return { bg: isLightMode ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.2)', text: isLightMode ? '#dc2626' : '#f87171', border: 'rgba(239, 68, 68, 0.4)' };
      case 'moved': return { bg: isLightMode ? 'rgba(245, 158, 11, 0.15)' : 'rgba(245, 158, 11, 0.2)', text: isLightMode ? '#d97706' : '#fbbf24', border: 'rgba(245, 158, 11, 0.4)' };
      case 'pending':
      default: return { bg: isLightMode ? 'rgba(100, 116, 139, 0.12)' : 'rgba(255, 255, 255, 0.05)', text: isLightMode ? '#475569' : '#94a3b8', border: isLightMode ? 'rgba(100, 116, 139, 0.2)' : 'rgba(255, 255, 255, 0.1)' };
    }
  };

  const getToastConfig = (status) => {
    switch (status) {
      case 'success': return { bg: '#22c55e', icon: FiCheckCircle };
      case 'error': return { bg: '#ef4444', icon: FiAlertCircle };
      case 'warning': return { bg: '#f59e0b', icon: FiAlertTriangle };
      default: return { bg: '#2563eb', icon: FiInfo };
    }
  };

  const triggerNotification = (msg, status = 'default') => {
    setToast({ show: true, message: msg, status: status });
    setTimeout(() => setToast({ show: false, message: "", status: 'default' }), 3000);
  };

  const requestConfirmation = (message, onConfirm) => setConfirmDialog({ message, onConfirm });
  const confirmPendingAction = () => {
    const onConfirm = confirmDialog?.onConfirm;
    setConfirmDialog(null);
    onConfirm?.();
  };

  const todaysPosts = [];
  projects.forEach(proj => {
    if (!proj.isArchived) {
      proj.posts.forEach(post => {
        if (!post.isArchived && post.date === todayStr) {
          todaysPosts.push({ ...post, projectName: proj.name, projectId: proj.id });
        }
      });
    }
  });

  const formatPhoneNumber = (value) => {
    let digits = value.replace(/\D/g, '');
    if (!digits.startsWith('998')) digits = '998' + digits;
    digits = digits.slice(0, 12);
    let formatted = '+998';
    if (digits.length > 3) formatted += ' (' + digits.slice(3, 5);
    if (digits.length > 5) formatted += ') ' + digits.slice(5, 8);
    if (digits.length > 8) formatted += '-' + digits.slice(8, 10);
    if (digits.length > 10) formatted += '-' + digits.slice(10, 12);
    return formatted;
  }

  const handlePlatformCheck = (name) => {
    setPlatformsData({ ...platformsData, [name]: { ...platformsData[name], checked: !platformsData[name].checked } });
  };
  const handleEditPlatformCheck = (name) => {
    setEditPlatformsData({ ...editPlatformsData, [name]: { ...editPlatformsData[name], checked: !editPlatformsData[name].checked } });
  };

  const handleAddProject = (e) => {
    e.preventDefault();
    if (!projName || phone.length < 19 || !targetBudget) return triggerNotification(t('projectList.alerts.fillAll'), 'error');
    const newProj = { id: Date.now(), name: projName, phone: phone, targetBudget: `$${targetBudget}`, isArchived: false, posts: [] };
    setProjects([...projects, newProj]);
    setSelectedProjectId(newProj.id);
    setProjName(""); setPhone(""); setTargetBudget("");
    setShowAddProjectModal(false);
    triggerNotification(t('projectList.notifications.projectAdded').replace('{name}', projName));
  };

  const openEditModal = (e, proj) => {
    e.stopPropagation();
    setEditProjectId(proj.id);
    setEditProjName(proj.name);
    setEditPhone(proj.phone);
    setEditTargetBudget(proj.targetBudget.replace(/\D/g, ''));
    setShowEditProjectModal(true);
  };

  const handleSaveEditProject = (e) => {
    e.preventDefault();
    if (!editProjName || editPhone.length < 19 || !editTargetBudget) return triggerNotification(t('projectList.alerts.fillAll'), 'error');
    setProjects(projects.map(p => p.id === editProjectId ? { ...p, name: editProjName, phone: editPhone, targetBudget: `$${editTargetBudget}` } : p));
    setShowEditProjectModal(false);
    triggerNotification(t('projectList.notifications.projectEdited'));
  };

  const handleArchiveProject = (e, proj) => {
    e.stopPropagation();
    setProjects(projects.map(p => p.id === proj.id ? { ...p, isArchived: true } : p));
    if (selectedProjectId === proj.id) setSelectedProjectId(null);
    triggerNotification(t('projectList.notifications.projectArchived').replace('{name}', proj.name), 'default');
  };

  const handleRestoreProject = (projectId) => {
    setProjects(projects.map(p => p.id === projectId ? { ...p, isArchived: false } : p));
    setSelectedProjectId(projectId);
    triggerNotification(t('projectList.notifications.projectRestored'), 'success');
  };

  const handleDeleteArchivedProject = (projectId, projectName) => {
    const confirmMessage = t('projectList.notifications.confirmDeleteProject').replace('{name}', projectName);
    requestConfirmation(confirmMessage, () => {
      setProjects(prev => prev.filter(project => project.id !== projectId));
      triggerNotification(t('projectList.notifications.projectDeleted'), 'success');
    });
  };

  const handleAddContent = (e) => {
    e.preventDefault();
    if (!postTitle || !postDate) return triggerNotification(t('projectList.alerts.titleDateRequired'), 'error');
    const selectedPlats = Object.keys(platformsData).filter(key => platformsData[key].checked).map(key => ({ name: key, status: platformsData[key].status }));
    if (selectedPlats.length === 0) return triggerNotification(t('projectList.alerts.selectPlatform'), 'error');

    const newPost = { id: Date.now(), title: postTitle, format: postFormat, date: postDate, idea: postIdea, isArchived: false, platforms: selectedPlats };
    setProjects(projects.map(proj => proj.id === Number(contentTargetProjectId) ? { ...proj, posts: [...proj.posts, newPost] } : proj));
    setPostTitle(""); setPostDate(""); setPostIdea("");
    setShowAddContentModal(false);
    triggerNotification(t('projectList.notifications.contentAdded'));
  };

  const handleArchivePost = (postId) => {
    setProjects(projects.map(proj => {
      if (proj.id === activeProject.id) {
        return { ...proj, posts: proj.posts.map(post => post.id === postId ? { ...post, isArchived: true } : post) };
      }
      return proj;
    }));
    triggerNotification(t('projectList.notifications.contentArchived'), 'default');
  };

  const handleRestorePost = (projId, postId) => {
    setProjects(projects.map(proj => {
      if (proj.id === projId) {
        return { ...proj, posts: proj.posts.map(post => post.id === postId ? { ...post, isArchived: false } : post) };
      }
      return proj;
    }));
    triggerNotification(t('projectList.notifications.contentRestored'), 'success');
  };

  const handleDeleteArchivedPost = (projectId, postId, postTitle) => {
    const confirmMessage = t('projectList.notifications.confirmDeleteContent').replace('{name}', postTitle);
    requestConfirmation(confirmMessage, () => {
      setProjects(prev => prev.map(project => project.id === projectId
        ? { ...project, posts: project.posts.filter(post => post.id !== postId) }
        : project));
      triggerNotification(t('projectList.notifications.contentDeleted'), 'success');
    });
  };

  const openEditContentModal = (post) => {
    setEditPostId(post.id);
    setEditPostTitle(post.title);
    setEditPostFormat(post.format);
    setEditPostDate(post.date);
    setEditPostIdea(post.idea || "");

    const initialPlatforms = {
      YouTube: { checked: false, status: "pending", color: "#ef4444" },
      Instagram: { checked: false, status: "pending", color: "#ec4899" },
      Facebook: { checked: false, status: "pending", color: "#1877f2" },
      Telegram: { checked: false, status: "pending", color: "#0ea5e9" }
    };
    post.platforms.forEach(p => {
      if (initialPlatforms[p.name]) {
         initialPlatforms[p.name].checked = true;
         initialPlatforms[p.name].status = p.status;
      }
    });
    setEditPlatformsData(initialPlatforms);
    setShowEditContentModal(true);
  };

  const handleSaveEditContent = (e) => {
    e.preventDefault();
    if (!editPostTitle || !editPostDate) return triggerNotification(t('projectList.alerts.titleDateRequired'), 'error');
    const selectedPlats = Object.keys(editPlatformsData).filter(key => editPlatformsData[key].checked).map(key => ({ name: key, status: editPlatformsData[key].status }));
    if (selectedPlats.length === 0) return triggerNotification(t('projectList.alerts.selectPlatform'), 'error');

    setProjects(projects.map(proj => {
      if (proj.id === activeProject.id) {
        return { ...proj, posts: proj.posts.map(post => post.id === editPostId ? { ...post, title: editPostTitle, format: editPostFormat, date: editPostDate, idea: editPostIdea, platforms: selectedPlats } : post) };
      }
      return proj;
    }));
    setShowEditContentModal(false);
    triggerNotification(t('projectList.notifications.contentEdited'));
  };

  const handleInlineStatusChange = (projId, postId, platName, newStatus) => {
    setProjects(prev => prev.map(proj => {
      if (proj.id === projId) {
        return { ...proj, posts: proj.posts.map(post => {
            if (post.id === postId) return { ...post, platforms: post.platforms.map(plat => plat.name === platName ? { ...plat, status: newStatus } : plat) };
            return post;
        })};
      }
      return proj;
    }));
    triggerNotification(t('projectList.notifications.statusChanged').replace('{platform}', platName), newStatus);
  };

  const handleExportPDF = () => {
    if (!activeProject) return;
    const activePosts = activeProject.posts.filter(p => !p.isArchived);

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const cyanColor = [0, 180, 216]; const navyColor = [26, 36, 83]; const blackColor = [15, 23, 42]; const gridColor = [180, 190, 200];  
    let yOffset = 25;

    doc.setFont("Helvetica", "Bold"); doc.setFontSize(12); doc.setTextColor(cyanColor[0], cyanColor[1], cyanColor[2]); doc.text(t('projectList.pdf.brand'), 15, yOffset);
    doc.setFont("Helvetica", "Normal"); doc.setTextColor(blackColor[0], blackColor[1], blackColor[2]); doc.text(` ${activeProject.name}`, 30, yOffset);
    yOffset += 8;
    doc.setFont("Helvetica", "Bold"); doc.setTextColor(cyanColor[0], cyanColor[1], cyanColor[2]); doc.text(t('projectList.pdf.phone'), 15, yOffset);
    doc.setFont("Helvetica", "Normal"); doc.setTextColor(blackColor[0], blackColor[1], blackColor[2]); doc.text(` ${activeProject.phone}`, 32, yOffset);
    
    const _pdfName = (() => { try { const s = localStorage.getItem(getUserKey('name')); return s ? JSON.parse(s) : 'Admin'; } catch { return 'Admin'; } })();
    doc.setFont("Helvetica", "Bold"); doc.setTextColor(cyanColor[0], cyanColor[1], cyanColor[2]); doc.text(t('projectList.pdf.fullName'), pageWidth - 70, 25);
    doc.setFont("Helvetica", "Normal"); doc.setTextColor(blackColor[0], blackColor[1], blackColor[2]); doc.text(` ${_pdfName}`, pageWidth - 57, 25);
    yOffset += 6;
    doc.setDrawColor(navyColor[0], navyColor[1], navyColor[2]); doc.setLineWidth(0.6); doc.line(15, yOffset, pageWidth - 15, yOffset);
    yOffset += 22;
    doc.setFont("Helvetica", "Bold"); doc.setFontSize(26); doc.setTextColor(navyColor[0], navyColor[1], navyColor[2]); doc.text(t('projectList.pdf.contentPlan'), pageWidth / 2, yOffset, { align: "center" });
    yOffset += 15;
    
    const startX = 15; const colWidths = [12, 40, 30, 68, 30]; const headers = ["№", "Post", "Format", "Idea", "Sana"];
    doc.setFillColor(navyColor[0], navyColor[1], navyColor[2]); doc.rect(startX, yOffset, 180, 11, "F");
    doc.setFont("Helvetica", "Bold"); doc.setFontSize(11); doc.setTextColor(255, 255, 255);
    let currentX = startX;
    headers.forEach((header, index) => { doc.text(header, currentX + colWidths[index] / 2, yOffset + 7, { align: "center" }); currentX += colWidths[index]; });
    doc.setDrawColor(navyColor[0], navyColor[1], navyColor[2]); doc.line(startX, yOffset, startX, yOffset + 11);
    currentX = startX; colWidths.forEach(w => { currentX += w; doc.line(currentX, yOffset, currentX, yOffset + 11); });
    yOffset += 11; doc.setTextColor(blackColor[0], blackColor[1], blackColor[2]);
    
    if (activePosts.length === 0) {
      doc.setFont("Helvetica", "Normal"); doc.setDrawColor(gridColor[0], gridColor[1], gridColor[2]); doc.rect(startX, yOffset, 180, 12);
      doc.text(t('projectList.pdf.empty'), startX + 5, yOffset + 8);
    } else {
      activePosts.forEach((post, index) => {
        doc.setFont("Helvetica", "Normal"); doc.setFontSize(10);
        const splitTitle = doc.splitTextToSize(post.title || "", colWidths[1] - 4);
        const splitIdea = doc.splitTextToSize(post.idea || "", colWidths[3] - 4);
        const maxLines = Math.max(splitTitle.length, splitIdea.length, 1);
        const rowHeight = Math.max(12, maxLines * 6.5);

        if (yOffset + rowHeight > 275) {
          doc.addPage(); yOffset = 20;
          doc.setFillColor(navyColor[0], navyColor[1], navyColor[2]); doc.rect(startX, yOffset, 180, 11, "F");
          doc.setFont("Helvetica", "Bold"); doc.setTextColor(255, 255, 255);
          let tx = startX; headers.forEach((h, i) => { doc.text(h, tx + colWidths[i] / 2, yOffset + 7, { align: "center" }); tx += colWidths[i]; });
          yOffset += 11; doc.setTextColor(blackColor[0], blackColor[1], blackColor[2]); doc.setFont("Helvetica", "Normal");
        }

        doc.setDrawColor(gridColor[0], gridColor[1], gridColor[2]); doc.setLineWidth(0.3); doc.rect(startX, yOffset, 180, rowHeight);
        let borderX = startX; colWidths.forEach(w => { borderX += w; doc.line(borderX, yOffset, borderX, yOffset + rowHeight); });
        let textX = startX;
        doc.text(`${index + 1}`, textX + colWidths[0] / 2, yOffset + (rowHeight / 2) + 3, { align: "center" }); textX += colWidths[0];
        doc.text(splitTitle, textX + 3, yOffset + 7); textX += colWidths[1];
        doc.text(`${post.format}`, textX + colWidths[2] / 2, yOffset + (rowHeight / 2) + 3, { align: "center" }); textX += colWidths[2];
        doc.text(splitIdea, textX + 3, yOffset + 7); textX += colWidths[3];
        doc.text(`${post.date}`, textX + colWidths[4] / 2, yOffset + (rowHeight / 2) + 3, { align: "center" });
        yOffset += rowHeight;
      });
    }
    doc.save(`${activeProject.name}_${t('projectList.pdf.filenameSuffix')}.pdf`);
    triggerNotification(t('projectList.notifications.pdfDownloaded').replace('{name}', activeProject.name), 'default');
  };

  const currentToastConfig = getToastConfig(toast.status);

  const inputStyle = {
    padding: '12px', borderRadius: '10px',
    border: isLight ? '1px solid rgba(0,0,0,0.12)' : '1px solid rgba(255,255,255,0.12)',
    background: isLight ? '#f8fafc' : 'rgba(255,255,255,0.03)', color: 'inherit', fontSize: '14px',
    outline: 'none', width: '100%', boxSizing: 'border-box', transition: 'border 0.2s'
  };
  const optionStyle = { background: isLight ? '#fff' : '#1e293b', color: isLight ? '#0f172a' : '#fff' };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: "easeOut" }} style={{ padding: isMobile ? '16px' : '30px', color: isLight ? '#0f172a' : '#fff', width: '100%', position: 'relative' }}>
      
      {/* TOAST BILDIRISHNOMA */}
      <AnimatePresence>
        {toast.show && (
          <motion.div
            initial={{ opacity: 0, y: -60, x: '-50%' }} animate={{ opacity: 1, y: 0, x: '-50%' }} exit={{ opacity: 0, y: -30, x: '-50%' }}
            style={{ position: 'fixed', top: '25px', left: '50%', background: currentToastConfig.bg, color: '#fff', padding: '12px 24px', borderRadius: '12px', zIndex: 100000, display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '600', fontSize: '14px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}
          >
            <currentToastConfig.icon size={18} strokeWidth={2} aria-hidden="true" /> <span>{toast.message}</span>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* SARLAVHA VA ARXIV TUGMASI */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
        <h1 style={{ fontSize: '26px', fontWeight: 'bold', margin: 0 }}>{t('projectList.title')}</h1>
        <motion.button 
          whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} 
          onClick={() => setIsArchiveDrawerOpen(true)}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: isMobile ? '8px 12px' : '10px 20px', borderRadius: '10px', border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.1)', background: isLight ? '#ffffff' : 'rgba(255,255,255,0.05)', color: isLight ? '#475569' : '#cbd5e1', fontWeight: '600', fontSize: isMobile ? '12px' : '14px', cursor: 'pointer' }}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="21 8 21 21 3 21 3 8"></polyline><rect x="1" y="3" width="22" height="5"></rect><line x1="10" y1="12" x2="14" y2="12"></line></svg>
          {!isMobile && t('analytics.archives')}
        </motion.button>
      </div>

      {/* BUGUNGI REJA PANELI */}
      <motion.div className="glass" style={{ padding: '20px', borderRadius: '16px', border: isLight ? '1px solid rgba(0,0,0,0.08)' : '1px solid rgba(255,255,255,0.1)', marginBottom: '25px' }}>
          <h3 style={{ fontSize: '15px', fontWeight: 'bold', margin: '0 0 15px 0', display: 'flex', alignItems: 'center', gap: '8px' }}><FiCalendar size={16} aria-hidden="true" /> {t('projectList.todayPlan')} ({todayStr})</h3>
        <motion.div variants={containerVariants} initial="hidden" animate="visible" style={{ display: 'flex', gap: '15px', overflowX: 'auto', paddingBottom: '10px' }}>
          {todaysPosts.length > 0 ? (
            <AnimatePresence>
              {todaysPosts.map((post) => (
                <motion.div key={post.id} layout variants={itemVariants} whileHover={{ y: -4, boxShadow: '0 8px 25px rgba(0,0,0,0.1)' }} exit={{ opacity: 0, scale: 0.8 }} style={{ minWidth: '260px', padding: '14px', borderRadius: '12px', background: isLight ? '#f8fafc' : 'rgba(255,255,255,0.04)', border: '1px solid rgba(0,0,0,0.05)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ fontSize: '11px', background: 'rgba(37, 99, 235, 0.15)', color: '#3b82f6', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>{post.projectName}</span>
                    <span style={{ fontSize: '11px', opacity: 0.6 }}>{post.format}</span>
                  </div>
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '13px' }}>{post.title}</h4>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {post.platforms.map((plat, idx) => {
                      const colors = getStatusColors(plat.status, isLight);
                      return (
                        <div key={idx} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '10px', background: colors.bg, color: colors.text, border: `1px solid ${colors.border}`, padding: '4px 6px', borderRadius: '4px', transition: 'all 0.2s' }}>
                          <PlatformIcon name={plat.name} size={12} />
                          <select value={plat.status} onChange={(e) => handleInlineStatusChange(post.projectId, post.id, plat.name, e.target.value)} style={{ background: 'transparent', border: 'none', color: 'inherit', fontSize: '10px', cursor: 'pointer', outline: 'none', fontWeight: '500' }}>
                            <option value="pending" style={optionStyle}>{t('projectList.statuses.pending')}</option>
                            <option value="published" style={optionStyle}>{t('projectList.statuses.published')}</option>
                            <option value="cancelled" style={optionStyle}>{t('projectList.statuses.cancelled')}</option>
                            <option value="moved" style={optionStyle}>{t('projectList.statuses.moved')}</option>
                          </select>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          ) : <p style={{ margin: 0, opacity: 0.5, fontSize: '13px' }}>{t('projectList.noToday')}</p>}
        </motion.div>
      </motion.div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1fr) minmax(320px, 1.5fr)', gap: isMobile ? '16px' : '25px', alignItems: 'start' }}>
        
        {/* CHAP USTUN: LOYIHALAR */}
          <motion.div layout className="glass" style={{ padding: '20px', borderRadius: '16px', border: isLight ? '1px solid rgba(0,0,0,0.08)' : '1px solid rgba(255,255,255,0.1)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 'bold', margin: 0 }}>{t('projectList.activeProjects')}</h3>
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => setShowAddProjectModal(true)} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '8px', border: 'none', background: 'var(--accent-color)', color: '#fff', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 10px rgba(var(--accent-color-rgb), 0.3)' }}><FiPlus size={15} />{t('projectList.addProject')}</motion.button>
          </div>
          <motion.div variants={containerVariants} initial="hidden" animate="visible" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <AnimatePresence>
              {visibleProjects.length > 0 ? visibleProjects.map((proj) => (
                <motion.div key={proj.id} layout variants={itemVariants} exit={{ opacity: 0, x: -20 }} onClick={() => setSelectedProjectId(proj.id)} whileHover={{ scale: 1.02, y: -2 }} style={{ padding: '12px 16px', borderRadius: '10px', cursor: 'pointer', background: selectedProjectId === proj.id ? 'var(--accent-color)' : (isLight ? '#f1f5f9' : 'rgba(255,255,255,0.04)'), color: selectedProjectId === proj.id ? '#fff' : 'inherit', boxShadow: selectedProjectId === proj.id ? '0 8px 20px rgba(var(--accent-color-rgb), 0.4)' : 'none', transition: 'box-shadow 0.2s' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <h4 style={{ margin: 0, fontSize: '14px', maxWidth: '70%', wordBreak: 'break-word' }}>{proj.name}</h4>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <motion.button whileHover={{ scale: 1.15, backgroundColor: isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.2)' }} whileTap={{ scale: 0.9 }} onClick={(e) => openEditModal(e, proj)} title={t('projectList.actions.edit')} aria-label={t('projectList.actions.edit')} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '5px', borderRadius: '6px', display: 'grid', placeItems: 'center', color: 'inherit' }}><FiEdit2 size={15} /></motion.button>
                      <motion.button whileHover={{ scale: 1.15, backgroundColor: isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.2)' }} whileTap={{ scale: 0.9 }} onClick={(e) => handleArchiveProject(e, proj)} title={t('projectList.actions.archive')} aria-label={t('projectList.actions.archive')} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '5px', borderRadius: '6px', display: 'grid', placeItems: 'center', color: 'inherit' }}><FiArchive size={15} /></motion.button>
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', opacity: 0.8, marginTop: '6px' }}>
                    <span>{t('projectList.labels.budget')}: {proj.targetBudget}</span>
                    <span>{getActiveContentText(proj.posts.filter(p => !p.isArchived).length)}</span>
                  </div>
                </motion.div>
              )) : <motion.p layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ fontSize: '13px', opacity: 0.5, textAlign: 'center', marginTop: '10px' }}>{t('projectList.noActive')}</motion.p>}
            </AnimatePresence>
          </motion.div>
        </motion.div>

        {/* O'NG USTUN: KONTENT REJA */}
        <motion.div layout className="glass" style={{ padding: '20px', borderRadius: '16px', border: isLight ? '1px solid rgba(0,0,0,0.08)' : '1px solid rgba(255,255,255,0.1)', minWidth: 0 }}>
          {activeProject ? (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1px solid rgba(0,0,0,0.08)', paddingBottom: '15px', marginBottom: '15px' }}>
                <div>
                  <h2 style={{ fontSize: '20px', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>{activeProject.name}</h2>
                  <p style={{ margin: '4px 0 0 0', fontSize: '12px', opacity: 0.7 }}>{t('projectList.labels.phone')}: {activeProject.phone} | {t('projectList.labels.budget')}: {activeProject.targetBudget}</p>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={handleExportPDF} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '8px', border: 'none', background: '#ef4444', color: '#fff', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 10px rgba(239,68,68,0.3)' }}><FiFileText size={15} />{t('projectList.buttons.exportPdf')}</motion.button>
                  <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => { setContentTargetProjectId(activeProject.id); setShowAddContentModal(true); }} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '8px', border: 'none', background: '#22c55e', color: '#fff', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 10px rgba(34,197,94,0.3)' }}><FiPlus size={15} />{t('projectList.buttons.addContent')}</motion.button>
                </div>
              </div>

              <h3 style={{ fontSize: '15px', marginBottom: '12px' }}>{t('projectList.labels.plannedContentList')}</h3>

              <motion.div variants={containerVariants} initial="hidden" animate="visible" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <AnimatePresence>
                  {visiblePosts.length > 0 ? (
                    visiblePosts.map((post, idx) => (
                      <motion.div key={post.id} layout variants={itemVariants} exit={{ opacity: 0, scale: 0.9, x: 20 }} whileHover={{ scale: 1.01, boxShadow: '0 5px 15px rgba(0,0,0,0.05)' }} style={{ padding: '14px', borderRadius: '12px', background: isLight ? '#f8fafc' : 'rgba(255,255,255,0.02)', border: '1px solid rgba(0,0,0,0.04)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>
                          <span>{idx + 1}. {post.title} <span style={{ fontWeight: 'normal', opacity: 0.5, fontSize: '11px' }}>({getFormatLabel(post.format)})</span></span>
                          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                            <span style={{ fontSize: '11px', opacity: 0.7, marginRight: '4px' }}>{post.date}</span>
                            <motion.button whileHover={{ scale: 1.15, backgroundColor: isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.2)' }} whileTap={{ scale: 0.9 }} onClick={() => openEditContentModal(post)} title={t('projectList.actions.edit')} aria-label={t('projectList.actions.edit')} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '5px', borderRadius: '6px', display: 'grid', placeItems: 'center', color: 'inherit' }}><FiEdit2 size={14} /></motion.button>
                            <motion.button whileHover={{ scale: 1.15, backgroundColor: isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.2)' }} whileTap={{ scale: 0.9 }} onClick={() => handleArchivePost(post.id)} title={t('projectList.actions.archive')} aria-label={t('projectList.actions.archive')} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '5px', borderRadius: '6px', display: 'grid', placeItems: 'center', color: 'inherit' }}><FiArchive size={14} /></motion.button>
                          </div>
                        </div>

                        {post.idea && <p style={{ margin: '0 0 10px 0', fontSize: '12px', opacity: 0.8, display: 'flex', alignItems: 'flex-start', gap: '6px' }}><FiInfo size={14} style={{ flexShrink: 0, marginTop: '2px' }} />{post.idea}</p>}
                        
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          {post.platforms.map((plat, i) => {
                            const colors = getStatusColors(plat.status, isLight);
                            return (
                              <div key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', background: colors.bg, color: colors.text, border: `1px solid ${colors.border}`, padding: '4px 8px', borderRadius: '6px', transition: 'all 0.2s' }}>
                                <PlatformIcon name={plat.name} size={12} />
                                <span style={{ color: 'inherit', fontWeight: '500' }}>{plat.name}:</span>
                                <select value={plat.status} onChange={(e) => handleInlineStatusChange(activeProject.id, post.id, plat.name, e.target.value)} style={{ background: 'transparent', border: 'none', color: 'inherit', fontSize: '11px', cursor: 'pointer', outline: 'none', fontWeight: '500' }}>
                                  <option value="pending" style={optionStyle}>{t('projectList.statuses.pending')}</option>
                                  <option value="published" style={optionStyle}>{t('projectList.statuses.published')}</option>
                                  <option value="cancelled" style={optionStyle}>{t('projectList.statuses.cancelled')}</option>
                                  <option value="moved" style={optionStyle}>{t('projectList.statuses.moved')}</option>
                                </select>
                              </div>
                            );
                          })}
                        </div>
                      </motion.div>
                    ))
                  ) : <motion.p layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ opacity: 0.5, fontSize: '13px', textAlign: 'center' }}>{t('projectList.labels.noActiveContent')}</motion.p>}
                </AnimatePresence>
              </motion.div>
            </motion.div>
          ) : <p style={{ opacity: 0.7 }}>{t('projectList.labels.projectNotSelected')}</p>}
        </motion.div>
      </div>

      {/* --- YANGI YON OYNA: ARXIV DRAWER (ANIMATSIYALASHTIRILGAN) --- */}
      <AnimatePresence>
        {isArchiveDrawerOpen && (
          <>
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}
              onClick={() => setIsArchiveDrawerOpen(false)}
              style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(5px)', zIndex: 40 }}
            />
            <motion.div
              initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: isMobile ? '100vw' : '400px', maxWidth: '100vw', background: isLight ? '#ffffff' : '#1e293b', borderLeft: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.1)', zIndex: 50, overflowY: 'auto', padding: isMobile ? '20px 16px' : '30px', boxShadow: '-5px 0 30px rgba(0,0,0,0.3)', color: isLight ? '#0f172a' : '#fff' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 'bold' }}>{t('analytics.archives')}</h2>
                <button onClick={() => setIsArchiveDrawerOpen(false)} aria-label={t('analytics.buttons.cancel')} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: isLight ? '#64748b' : '#cbd5e1' }}>×</button>
              </div>

              {/* TAB NAVIGATSIYASI */}
              <div style={{ display: 'flex', gap: '10px', marginBottom: '25px', background: isLight ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)', padding: '6px', borderRadius: '14px' }}>
                <motion.button 
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setArchiveTab('projects')}
                  style={{ flex: 1, padding: '10px', borderRadius: '10px', border: 'none', background: archiveTab === 'projects' ? '#3b82f6' : 'transparent', color: archiveTab === 'projects' ? '#fff' : 'inherit', fontWeight: 'bold', cursor: 'pointer', transition: 'background 0.3s', fontSize: '13px' }}
                >
                  <FiFolder size={15} aria-hidden="true" /> {t('projectList.archiveTabs.projects')}
                </motion.button>
                <motion.button 
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setArchiveTab('contents')}
                  style={{ flex: 1, padding: '10px', borderRadius: '10px', border: 'none', background: archiveTab === 'contents' ? '#a855f7' : 'transparent', color: archiveTab === 'contents' ? '#fff' : 'inherit', fontWeight: 'bold', cursor: 'pointer', transition: 'background 0.3s', fontSize: '13px' }}
                >
                  <FiFileText size={15} aria-hidden="true" /> {t('projectList.archiveTabs.contents')}
                </motion.button>
              </div>

              {/* TAB MA'LUMOTLARI ANIMATSIYASI */}
              <div style={{ position: 'relative' }}>
                <AnimatePresence mode="wait">
                  {archiveTab === 'projects' ? (
                    <motion.div key="projects" variants={tabVariants} initial="hidden" animate="visible" exit="exit" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <AnimatePresence>
                        {archivedProjects.length > 0 ? archivedProjects.map(proj => (
                          <motion.div layout initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} transition={{ duration: 0.2 }} key={proj.id} style={{ padding: '15px', borderRadius: '12px', background: isLight ? '#fff' : 'rgba(255,255,255,0.03)', border: isLight ? '1px solid rgba(0,0,0,0.08)' : '1px solid rgba(255,255,255,0.08)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div>
                                <h4 style={{ margin: '0 0 4px 0', fontSize: '14px' }}>{proj.name}</h4>
                                <span style={{ fontSize: '12px', opacity: 0.6 }}>{t('projectList.labels.totalContent').replace('{count}', String(proj.posts.length))}</span>
                              </div>
                              <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                                <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => handleRestoreProject(proj.id)} style={{ padding: '6px 10px', background: 'rgba(34, 197, 94, 0.15)', color: '#22c55e', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <FiRotateCcw size={14} aria-hidden="true" /> {t('projectList.buttons.restore')}
                                </motion.button>
                                <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => handleDeleteArchivedProject(proj.id, proj.name)} title={t('projectList.buttons.delete')} aria-label={t('projectList.buttons.delete')} style={{ padding: '6px 10px', background: 'rgba(239, 68, 68, 0.12)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.25)', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>
                                  <FiTrash2 size={14} aria-hidden="true" /> {t('projectList.buttons.delete')}
                                </motion.button>
                              </div>
                            </div>
                          </motion.div>
                        )) : <motion.p layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ fontSize: '13px', opacity: 0.5, textAlign: 'center', marginTop: '20px' }}>{t('projectList.labels.archiveEmptyProjects')}</motion.p>}
                      </AnimatePresence>
                    </motion.div>
                  ) : (
                    <motion.div key="contents" variants={tabVariants} initial="hidden" animate="visible" exit="exit" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <AnimatePresence>
                        {archivedPosts.length > 0 ? archivedPosts.map(post => (
                          <motion.div layout initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} transition={{ duration: 0.2 }} key={post.id} style={{ padding: '15px', borderRadius: '12px', background: isLight ? '#fff' : 'rgba(255,255,255,0.03)', border: isLight ? '1px solid rgba(0,0,0,0.08)' : '1px solid rgba(255,255,255,0.08)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                              <div style={{ maxWidth: '70%' }}>
                                <span style={{ fontSize: '10px', background: 'rgba(168, 85, 247, 0.15)', color: '#a855f7', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>{post.projectName}</span>
                                <h4 style={{ margin: '6px 0 0 0', fontSize: '13px', wordBreak: 'break-word' }}>{post.title}</h4>
                              </div>
                              <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                                <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => handleRestorePost(post.projectId, post.id)} style={{ padding: '6px 10px', background: 'rgba(34, 197, 94, 0.15)', color: '#22c55e', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <FiRotateCcw size={14} aria-hidden="true" /> {t('projectList.buttons.restore')}
                                </motion.button>
                                <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => handleDeleteArchivedPost(post.projectId, post.id, post.title)} title={t('projectList.buttons.delete')} aria-label={t('projectList.buttons.delete')} style={{ padding: '6px 10px', background: 'rgba(239, 68, 68, 0.12)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.25)', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>
                                  <FiTrash2 size={14} aria-hidden="true" /> {t('projectList.buttons.delete')}
                                </motion.button>
                              </div>
                            </div>
                            <span style={{ fontSize: '11px', opacity: 0.6 }}>{t('projectList.pdf.date')} {post.date}</span>
                          </motion.div>
                        )) : <motion.p layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ fontSize: '13px', opacity: 0.5, textAlign: 'center', marginTop: '20px' }}>{t('projectList.labels.archiveEmptyContents')}</motion.p>}
                      </AnimatePresence>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* QOLGAN MODALLAR (Qo'shish va Tahrirlash uchun) */}
      <AnimatePresence>
        {showAddProjectModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(10px)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center' }} onClick={() => setShowAddProjectModal(false)}>
            <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }} transition={{ type: 'spring', damping: 25, stiffness: 300 }} className="glass" style={{ width: isMobile ? 'calc(100vw - 32px)' : '380px', padding: isMobile ? '20px 16px' : '28px', borderRadius: '20px', background: isLight ? '#fff' : '#1e293b', border: isLight ? '1px solid rgba(0,0,0,0.1)' : '1px solid rgba(255,255,255,0.1)', boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
              <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '9px' }}><FiFolder size={18} />{t('projectList.modalTitles.addProject')}</h3>
              <form onSubmit={handleAddProject} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <input type="text" placeholder={t('projectList.placeholders.projectName')} value={projName} onChange={e => setProjName(e.target.value)} style={inputStyle} />
                <input type="text" placeholder={t('projectList.placeholders.phone')} value={phone} onChange={(e) => setPhone(formatPhoneNumber(e.target.value))} style={inputStyle} />
                <input type="number" placeholder={t('projectList.placeholders.budget')} value={targetBudget} onChange={e => setTargetBudget(e.target.value)} style={inputStyle} />
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '8px' }}>
                  <button type="button" onClick={() => setShowAddProjectModal(false)} style={{ padding: '10px 16px', background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: '14px' }}>{t('projectList.buttons.cancel')}</button>
                  <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} type="submit" style={{ padding: '10px 22px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer', fontSize: '14px' }}>{t('projectList.buttons.save')}</motion.button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showEditProjectModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(10px)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center' }} onClick={() => setShowEditProjectModal(false)}>
            <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }} transition={{ type: 'spring', damping: 25, stiffness: 300 }} className="glass" style={{ width: isMobile ? 'calc(100vw - 32px)' : '380px', padding: isMobile ? '20px 16px' : '28px', borderRadius: '20px', background: isLight ? '#fff' : '#1e293b', border: isLight ? '1px solid rgba(0,0,0,0.1)' : '1px solid rgba(255,255,255,0.1)', boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
              <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '9px' }}><FiEdit2 size={17} />{t('projectList.modalTitles.editProject')}</h3>
              <form onSubmit={handleSaveEditProject} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <input type="text" placeholder={t('projectList.placeholders.projectName')} value={editProjName} onChange={e => setEditProjName(e.target.value)} style={inputStyle} />
                <input type="text" placeholder={t('projectList.placeholders.phone')} value={editPhone} onChange={(e) => setEditPhone(formatPhoneNumber(e.target.value))} style={inputStyle} />
                <input type="number" placeholder={t('projectList.placeholders.budget')} value={editTargetBudget} onChange={e => setEditTargetBudget(e.target.value)} style={inputStyle} />
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '8px' }}>
                  <button type="button" onClick={() => setShowEditProjectModal(false)} style={{ padding: '10px 16px', background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: '14px' }}>{t('projectList.buttons.cancel')}</button>
                  <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} type="submit" style={{ padding: '10px 22px', background: '#22c55e', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer', fontSize: '14px' }}>{t('projectList.buttons.save')}</motion.button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showAddContentModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(10px)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center' }} onClick={() => setShowAddContentModal(false)}>
            <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }} transition={{ type: 'spring', damping: 25, stiffness: 300 }} className="glass" style={{ width: isMobile ? 'calc(100vw - 32px)' : '440px', maxHeight: isMobile ? '90dvh' : 'unset', overflowY: isMobile ? 'auto' : 'visible', padding: isMobile ? '20px 16px' : '28px', borderRadius: '20px', background: isLight ? '#fff' : '#1e293b', border: isLight ? '1px solid rgba(0,0,0,0.1)' : '1px solid rgba(255,255,255,0.1)', boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
              <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '9px' }}><FiFileText size={17} />{t('projectList.modalTitles.addContent')}</h3>
              <form onSubmit={handleAddContent} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <select value={contentTargetProjectId} onChange={e => setContentTargetProjectId(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
                  {projects.filter(p => !p.isArchived).map(p => <option key={p.id} value={p.id} style={optionStyle}>{p.name}</option>)}
                </select>
                <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: '12px' }}>
                  <input type="text" placeholder={t('projectList.placeholders.contentTitle')} value={postTitle} onChange={e => setPostTitle(e.target.value)} style={inputStyle} />
                  <div style={{ position: 'relative' }}>
                    <div onClick={() => setIsFormatOpen(!isFormatOpen)} style={{ ...inputStyle, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>{getFormatLabel(postFormat)}</span> <span style={{ fontSize: '10px', opacity: 0.6 }}>▼</span>
                    </div>
                    {isFormatOpen && (
                      <div style={{ position: 'absolute', top: '46px', left: 0, width: '100%', background: isLight ? '#fff' : '#1e293b', border: isLight ? '1px solid rgba(0,0,0,0.1)' : '1px solid rgba(255,255,255,0.15)', borderRadius: '10px', zIndex: 10, boxShadow: '0 10px 25px rgba(0,0,0,0.15)', overflow: 'hidden' }}>
                        {formatOptions.map(opt => <div key={opt} onClick={() => { setPostFormat(opt); setIsFormatOpen(false); }} style={{ padding: '10px 14px', cursor: 'pointer', fontSize: '14px', transition: 'background 0.2s' }} onMouseEnter={(e) => e.target.style.background = isLight ? '#f1f5f9' : 'rgba(255,255,255,0.05)'} onMouseLeave={(e) => e.target.style.background = 'transparent'}>{getFormatLabel(opt)}</div>)}
                      </div>
                    )}
                  </div>
                </div>
                <input type="date" value={postDate} onChange={e => setPostDate(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }} />
                <textarea placeholder={t('projectList.placeholders.contentIdea')} value={postIdea} onChange={e => setPostIdea(e.target.value)} style={{ ...inputStyle, minHeight: '75px', resize: 'none', fontFamily: 'inherit' }} />
                
                <div>
                  <label style={{ fontSize: '13px', fontWeight: '600', display: 'block', marginBottom: '8px', opacity: 0.8 }}>Platformalar:</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    {Object.keys(platformsData).map(key => (
                      <div key={key} onClick={() => handlePlatformCheck(key)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', borderRadius: '10px', background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.04)', border: platformsData[key].checked ? '1px solid #2563eb' : '1px solid transparent', cursor: 'pointer', transition: 'all 0.2s' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: '500' }}><PlatformIcon name={key} size={14} /> {key}</span>
                        <input type="checkbox" checked={platformsData[key].checked} onChange={() => {}} style={{ cursor: 'pointer', pointerEvents: 'none' }} />
                      </div>
                    ))} 
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '10px' }}>
                  <button type="button" onClick={() => setShowAddContentModal(false)} style={{ padding: '10px 16px', background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: '14px' }}>{t('projectList.buttons.cancel')}</button>
                  <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} type="submit" style={{ padding: '10px 24px', background: '#22c55e', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer', fontSize: '14px' }}>{t('projectList.buttons.save')}</motion.button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showEditContentModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(10px)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center' }} onClick={() => setShowEditContentModal(false)}>
            <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }} transition={{ type: 'spring', damping: 25, stiffness: 300 }} className="glass" style={{ width: isMobile ? 'calc(100vw - 32px)' : '440px', maxHeight: isMobile ? '90dvh' : 'unset', overflowY: isMobile ? 'auto' : 'visible', padding: isMobile ? '20px 16px' : '28px', borderRadius: '20px', background: isLight ? '#fff' : '#1e293b', border: isLight ? '1px solid rgba(0,0,0,0.1)' : '1px solid rgba(255,255,255,0.1)', boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
              <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '9px' }}><FiEdit2 size={17} />{t('projectList.modalTitles.editContent')}</h3>
              <form onSubmit={handleSaveEditContent} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: '12px' }}>
                  <input type="text" placeholder={t('projectList.placeholders.contentTitle')} value={editPostTitle} onChange={e => setEditPostTitle(e.target.value)} style={inputStyle} />
                  <div style={{ position: 'relative' }}>
                    <div onClick={() => setIsEditFormatOpen(!isEditFormatOpen)} style={{ ...inputStyle, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>{getFormatLabel(editPostFormat)}</span> <span style={{ fontSize: '10px', opacity: 0.6 }}>▼</span>
                    </div>
                    {isEditFormatOpen && (
                      <div style={{ position: 'absolute', top: '46px', left: 0, width: '100%', background: isLight ? '#fff' : '#1e293b', border: isLight ? '1px solid rgba(0,0,0,0.1)' : '1px solid rgba(255,255,255,0.15)', borderRadius: '10px', zIndex: 10, boxShadow: '0 10px 25px rgba(0,0,0,0.15)', overflow: 'hidden' }}>
                        {formatOptions.map(opt => <div key={opt} onClick={() => { setEditPostFormat(opt); setIsEditFormatOpen(false); }} style={{ padding: '10px 14px', cursor: 'pointer', fontSize: '14px', transition: 'background 0.2s' }} onMouseEnter={(e) => e.target.style.background = isLight ? '#f1f5f9' : 'rgba(255,255,255,0.05)'} onMouseLeave={(e) => e.target.style.background = 'transparent'}>{getFormatLabel(opt)}</div>)}
                      </div>
                    )}
                  </div>
                </div>
                <input type="date" value={editPostDate} onChange={e => setEditPostDate(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }} />
                <textarea placeholder={t('projectList.placeholders.contentIdea')} value={editPostIdea} onChange={e => setEditPostIdea(e.target.value)} style={{ ...inputStyle, minHeight: '75px', resize: 'none', fontFamily: 'inherit' }} />
                
                <div>
                  <label style={{ fontSize: '13px', fontWeight: '600', display: 'block', marginBottom: '8px', opacity: 0.8 }}>Platformalar:</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    {Object.keys(editPlatformsData).map(key => (
                      <div key={key} onClick={() => handleEditPlatformCheck(key)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', borderRadius: '10px', background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.04)', border: editPlatformsData[key].checked ? '1px solid #2563eb' : '1px solid transparent', cursor: 'pointer', transition: 'all 0.2s' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: '500' }}><PlatformIcon name={key} size={14} /> {key}</span>
                        <input type="checkbox" checked={editPlatformsData[key].checked} onChange={() => {}} style={{ cursor: 'pointer', pointerEvents: 'none' }} />
                      </div>
                    ))} 
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '10px' }}>
                  <button type="button" onClick={() => setShowEditContentModal(false)} style={{ padding: '10px 16px', background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: '14px' }}>{t('projectList.buttons.cancel')}</button>
                  <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} type="submit" style={{ padding: '10px 24px', background: '#22c55e', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer', fontSize: '14px' }}>{t('projectList.buttons.save')}</motion.button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {confirmDialog && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setConfirmDialog(null)}
            style={{ position: 'fixed', inset: 0, zIndex: 100001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', background: 'rgba(2, 6, 23, 0.62)', backdropFilter: 'blur(8px)' }}
          >
            <motion.div
              role="alertdialog" aria-modal="true" aria-labelledby="project-delete-title" aria-describedby="project-delete-message"
              initial={{ opacity: 0, y: 14, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 8, scale: 0.98 }}
              onClick={event => event.stopPropagation()}
              style={{ width: '100%', maxWidth: '420px', padding: '24px', borderRadius: '18px', background: isLight ? '#ffffff' : '#1e293b', color: isLight ? '#0f172a' : '#f8fafc', border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.1)', boxShadow: '0 24px 70px rgba(0,0,0,0.38)' }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                <div style={{ width: '44px', height: '44px', flexShrink: 0, display: 'grid', placeItems: 'center', borderRadius: '13px', background: isLight ? '#fef2f2' : 'rgba(239,68,68,0.14)', color: isLight ? '#dc2626' : '#f87171' }}>
                  <FiAlertTriangle size={21} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <h2 id="project-delete-title" style={{ margin: '2px 0 8px', fontSize: '17px', fontWeight: 750 }}>{t('projectList.confirmations.deleteTitle')}</h2>
                  <p id="project-delete-message" style={{ margin: 0, color: isLight ? '#475569' : '#cbd5e1', fontSize: '14px', lineHeight: 1.6, overflowWrap: 'anywhere' }}>{confirmDialog.message}</p>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '24px' }}>
                <button type="button" onClick={() => setConfirmDialog(null)} style={{ padding: '10px 16px', borderRadius: '9px', border: isLight ? '1px solid #cbd5e1' : '1px solid #475569', background: 'transparent', color: 'inherit', fontWeight: 650, cursor: 'pointer' }}>{t('projectList.buttons.cancel')}</button>
                <button type="button" onClick={confirmPendingAction} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '10px 16px', borderRadius: '9px', border: '1px solid #dc2626', background: '#dc2626', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
                  <FiTrash2 size={15} /> {t('projectList.buttons.delete')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </motion.div>
  );
};

export default ProjectList;